import { baseSuccessToastMessage } from "@/primevue/init";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import { api, types } from "@/lib/openapi";
import {
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";

export type DomainInTree = types.DomainInTree;

export const useDomainsStore = defineStore("domains", () => {
    const { sync } = useWorktreeStore();
    const { isFetching, worktree } = useWorktreeManager();
    const isLoading = ref(false);
    const toast = useToast();

    // We're using the worktree, which contains the domains, to avoid another fetch.
    const domains = computed<DomainInTree[]>(() => {
        if (!worktree.value) {
            return [];
        }

        return worktree.value.domains.sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    });

    /**
     * Create a new domain.
     */
    const create = async (name: string) => {
        isLoading.value = true;
        const { data, response } =
            await api.startDomainDomainMgtCreateDomainStartPost({
                body: {
                    name: name,
                },
            });
        if (response.ok) {
            await sync();
        } else {
            toast.add({
                severity: "error",
                summary: "Could not create domain",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return data;
    };

    /**
     * Deletes the given Domain.
     */
    async function remove(
        domainId: string,
        opts: { showToast: boolean } = { showToast: true }
    ) {
        isLoading.value = true;
        const { response } =
            await api.startDomainDomainMgtDeleteDomainStartPost({
                body: {
                    domain_id: domainId,
                },
            });
        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Domain ${domainId} deleted`,
                });
            }
            await sync();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete domain: " + domainId,
                    detail: "Server returned " + response.status,
                });
            }
        }
        isLoading.value = false;
    }

    return { create, remove, domains, isLoading, isFetching };
});

export const useDomainsManager = () => storeToRefs(useDomainsStore());
